import { Token, KEYWORDS, OPERATORS, GROUPING } from './tokens';
import type { WithRange } from './types';
import * as T from './tokens';

const sanatizeForRegex = (token: string): string =>
  [...token]
    .map((char) => ('/.*+?|(){}[]^'.includes(char) ? `\\${char}` : char))
    .join('');

const regex = (literals: readonly string[]) =>
  new RegExp(`^(${literals.map((op) => sanatizeForRegex(op)).join('|')})`);

const lexDate = (match: string): Token => {
  const num = Date.parse(`${match.slice(1)}Z`);
  return isNaN(num) ? T.unknown(match) : T.lit(new Date(num));
};

type LexRule<Token> = [RegExp, (str: string) => Token];

// Note: Comment and whitespace patterns need to be processed in the specified order.
const LEX_RULES: LexRule<Token>[] = [
  // Comments
  [
    /^\n\s*\/\/.*?(?=\n|$)/,
    (t) => T.comment(/\/\/.*/.exec(t)![0].slice(2), 'leading'),
  ],
  [/^\/\/.*?(?=\n|$)/, (t) => T.comment(t.slice(2, t.length), 'trailing')],
  // White Space
  [/^\s+/, T.ws],
  // Ident
  [/^".*?"/, (t) => T.ident(t.slice(1, -1))],
  // String Literal
  [/^'(?:[^'\\]|\\.)*?'/, (t) => T.lit(t.slice(1, -1).replaceAll(`\\'`, `'`))],
  // Number literal
  [/^-?\d+(\.\d+)?/, (token) => T.lit(Number(token))],
  // Bool Literal
  [/^(true|false)/, (x) => T.lit(x === 'true')],
  // Day Literal
  [/^@[\d-]+/, lexDate],
  // Operator
  [regex(OPERATORS), T.op],
  // Grouping
  [regex(GROUPING.filter((x) => x !== '{' && x !== '}')), T.group],
  // Symbol / Keyword
  [
    /^[$%a-zA-Z_][a-zA-Z0-9-_]*/,
    (x) => (KEYWORDS.includes(x as any) ? T.kw(x) : T.sym(x)),
  ],
  // Tag
  [
    /^@(".*?"|[a-zA-Z0-9-_]+)/,
    (x) => T.tag(x.slice(1).replace(/^"/, '').replace(/"$/, '')),
  ],
];

// eslint-disable-next-line
export function* lex(
  str: string,
  opts?: { offset?: number }
): Generator<WithRange<Token>, void> {
  const { offset = 0 } = opts ?? {};
  let cursor = 0;
  const bracketStack: ('{' | '{{')[] = [];

  // Edge case where a leading comment appears at the top of a file (where there is not neccessarily a preceeding newline)
  const programLevelComment = /^\s*\/\/.*?(?=\n|$)/.exec(str);
  if (programLevelComment) {
    const match = programLevelComment[0];
    const end = cursor + match.length;

    yield [
      T.comment(/\/\/.*/.exec(match)![0].slice(2), 'leading'),
      { range: [cursor + offset, end + offset] },
    ];

    cursor = end;
  }

  outer: while (cursor < (str?.length ?? 0)) {
    const unprocessed = str.slice(cursor);

    // Match complete template string, handling escaped backticks
    // Pattern: ` followed by content (allowing \` for escaped backticks) until unescaped `
    const completeTemplate = /^`((?:[^`\\]|\\.)*?)`/.exec(unprocessed);

    if (completeTemplate) {
      const match = completeTemplate[0];
      if (!match.includes('{{')) {
        const end = cursor + match.length;
        // Unescape backticks in the content: \` becomes `
        const content = match.slice(1, -1).replaceAll('\\`', '`');
        yield [
          T.tComplete(content),
          { range: [cursor + offset, end + offset] },
        ];
        cursor = end;
        continue outer;
      }
    }

    // Match template string start, handling escaped backticks
    // Pattern: ` followed by content until {{ (allowing \` for escaped backticks)
    const startTemplate = /^`((?:[^`\\]|\\.)*?){{/.exec(unprocessed);

    if (startTemplate) {
      bracketStack.push('{{');
      const match = startTemplate[0];

      const end = cursor + match.length;
      // Unescape backticks in the content: \` becomes `
      const content = match.slice(1, -2).replaceAll('\\`', '`');
      yield [T.tStart(content), { range: [cursor + offset, end + offset] }];
      cursor = end;
      continue outer;
    }

    if (bracketStack.at(-1) === '{{') {
      // Match template string end, handling escaped backticks
      // Pattern: }} followed by content until ` (allowing \` for escaped backticks)
      const endTemplate = /^}}((?:[^`\\]|\\.)*?)`/.exec(unprocessed);

      if (endTemplate) {
        const match = endTemplate[0];
        if (!match.includes('{{')) {
          bracketStack.pop();
          const end = cursor + match.length;
          // Unescape backticks in the content: \` becomes `
          const content = match.slice(2, -1).replaceAll('\\`', '`');
          yield [T.tEnd(content), { range: [cursor + offset, end + offset] }];
          cursor = end;
          continue outer;
        }
      }

      const continueTemplate = /^}}((?:[^`\\]|\\.)*?){{/.exec(unprocessed);

      if (continueTemplate) {
        const match = continueTemplate[0];
        const end = cursor + match.length;
        // Unescape backticks in the content: \` becomes `
        const content = match.slice(2, -2).replaceAll('\\`', '`');
        yield [
          T.tContinue(content),
          { range: [cursor + offset, end + offset] },
        ];
        cursor = end;
        continue outer;
      }
    }

    const next = unprocessed[0];

    if (next === '{') {
      bracketStack.push(next);
      const end = cursor + next.length;
      yield [T.group(next), { range: [cursor + offset, end + offset] }];
      cursor = end;
      continue outer;
    }

    if (bracketStack.at(-1) === '{' && next === '}') {
      bracketStack.pop();
      const end = cursor + next.length;
      yield [T.group(next), { range: [cursor + offset, end + offset] }];
      cursor = end;
      continue outer;
    }

    for (const rule of LEX_RULES) {
      const [match, process] = rule;
      const found = match.exec(unprocessed);
      if (found) {
        const rawToken = found[0];
        const end = cursor + rawToken.length;
        const token = process(rawToken);
        yield [token, { range: [cursor + offset, end + offset] }];
        cursor = end;
        continue outer;
      }
    }

    break;
  }

  if (cursor < (str?.length ?? 0)) {
    yield [
      { t: 'unknown', found: str.slice(cursor) },
      { range: [cursor, str.length] },
    ];
  }

  return;
}
